<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'
import { useStrapiVendorList } from '@nimbus/composables/strapi/useStrapiVendorList'
import { vIntersectionObserver } from '@vueuse/components'
import { type SearchResults, ref, useGameSearch, useI18n, useTracking } from '#imports'

export interface GameVendor {
    name: string
    slug: string
    providerSlug: string | undefined
    useProvider: boolean
    useProviderSlug: boolean
}

withDefaults(defineProps<{
    title?: string
    invertLogos?: boolean
    static?: boolean
}>(), {
    invertLogos: false,
    static: false
})

const { vendor } = useGameSearch()
const { isLoading, search } = vendor()
const { vendorList } = useStrapiVendorList()
const { t } = useI18n()

const selectedVendorList = ref(new Set<GameVendor>())
const gamesList = ref<SearchResults | null>({
    success: false,
    matches: [],
    suggestions: []
})
const vendorListResult: Array<GameVendor> = await vendorList({ server: true })

const { trackEvent } = useTracking()
function trackGameSearch(value: string, type: string) {
    void trackEvent({
        event_name: `game_search`,
        event_type: type,
        meta_data: [{ value }],
        log_entry: true
    }) as void
}

/**
 * Searches for games based on currently selected vendors/providers.
 */
async function searchSelectedVendors(): Promise<void> {
    if (selectedVendorList.value.size === 0) {
        gamesList.value = {
            success: false,
            suggestions: [],
            matches: [],
        }
        return
    }

    // Separate vendors and providers based on useProvider flag
    const vendors = Array.from(selectedVendorList.value)
        .filter(x => !x.useProvider)
        .map(x => x.slug)
    const providers = Array.from(selectedVendorList.value)
        .filter(x => x.useProvider && x.providerSlug)
        .map(x => x.providerSlug!)

    gamesList.value = await search({ vendors, providers })
}

/**
 * Handles the selection of all vendors.
 */
async function onSelectAllHandler(): Promise<void> {
    if (selectedVendorList.value.size === vendorListResult.length) {
        selectedVendorList.value.clear()
    }
    else {
        vendorListResult.forEach(vendor => selectedVendorList.value.add(vendor))
        trackGameSearch('all', 'vendor_filter')
    }

    await searchSelectedVendors()
}

/**
 * Handles the selection of individual vendors.
 * @param vendor {GameVendor}
 */
async function onSelectVendorHandler(vendor: GameVendor): Promise<void> {
    if (selectedVendorList.value.has(vendor)) {
        selectedVendorList.value.delete(vendor)
        trackGameSearch(vendor.slug, 'remove_vendor_filter')
    }
    else {
        selectedVendorList.value.add(vendor)
        trackGameSearch(vendor.slug, 'add_vendor_filter')
    }

    await searchSelectedVendors()
}

const showProviders = ref(false)

function animateInProviders(entries: IntersectionObserverEntry[]) {
    const { isIntersecting } = entries[0]!
    if (!isIntersecting || showProviders.value) return
    showProviders.value = true
}
</script>

<template>
    <ClientOnly>
        <section
            v-if="!isEmpty(vendorListResult)"
            v-intersection-observer.once="[animateInProviders, { threshold: 0.1 }]"
            class="game-search"
            :class="{ 'animate-in': showProviders }"
        >
            <div v-if="(title?.length ?? 0) > 0 || !static" class="game-search__title">
                {{ title }}
            </div>

            <div
                :class="[
                    'game-search__vendor-list',
                    { 'game-search__vendor-list--static': static }
                ]"
            >
                <div
                    v-if="!static"
                    :class="[
                        'game-search__vendor',
                    ]"
                    @click="onSelectAllHandler()"
                >
                    <strong>{{ t('core.showAll') }}</strong>
                </div>

                <div
                    v-for="vendorItem in vendorListResult"
                    :key="`${vendorItem.name}-${vendorItem.slug}`"
                    :class="[
                        'game-search__vendor',
                        { 'game-search__vendor--selected': selectedVendorList.has(vendorItem) },
                        { 'game-search__vendor--static': static }
                    ]"
                    @click="!static && onSelectVendorHandler(vendorItem)"
                >
                    <NuxtImg
                        :class="[
                            'game-search__vendor-logo',
                            invertLogos ? 'logo--dark' : undefined
                        ]"
                        :src="`/logo/vendors/${vendorItem.slug}.png`"
                        preset="gameservice"
                        provider="cloudflare"
                        loading="lazy"
                        decoding="async"
                        sizes="sm:100px md:132px"
                        width="100"
                    />
                </div>
            </div>

            <GameList
                v-if="!isLoading && selectedVendorList && gamesList?.matches && gamesList.matches.length > 0"
                category="matches"
                :games="gamesList?.matches"
                :title="`Games By ${Array.from(selectedVendorList).map(x => x.name).join(', ')}`"
                :video-enabled="true"
                :skeletons="10"
            />

            <UILoader
                v-if="isLoading"
                height="40px"
                width="40px"
                class="game-search__loader"
                :text="false"
                :inline="true"
            />
        </section>
    </ClientOnly>
</template>

<style lang="scss" scoped>
.game-search {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 0;

    @include tablet {
        gap: 20px;
    }

    &__title {
        font-size: $font-size-xxl;
        font-weight: $text-bold;
        text-align: center;
    }

    &__name {
        font-weight: $text-bold;
    }

    &__vendor-list {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 10px;

        &--static {
            margin-top: 30px;
            margin-bottom: 80px;
        }

        @include tablet {
            grid-template-columns: repeat(auto-fit, minmax(150px, 132px));
        }
    }

    &__vendor {
        display: flex;
        align-items: center;
        justify-content: center;

        background-color: $btn-outlined-bg-color;
        border: 2px solid transparent;
        border-radius: var(--border-radius-lg, $border-radius-lg);
        transition: all 240ms;
        cursor: pointer;

        padding: 10px;
        width: 132px;
        height: 64px;

        opacity: 0;
        transform: translate3d(0, 80px, 0);
        backface-visibility: hidden;
        will-change: transform, opacity;
        transition:
            opacity 0.3s ease,
            transform 1s ease-out;

        @include tablet {
            height: 80px;
        }

        @media (hover: hover) {
            &:not(.game-search__vendor--static):hover {
                scale: 1.05;
                background-color: $game-search-field-bg;
                border: 2px solid $game-search-field-border-focus;

                transition: all 240ms;
            }
        }

        &--selected {
            background-color: $game-search-field-bg;
            border: 2px solid $game-search-field-border-focus;
        }

        &--static {
            cursor: default;
            background: rgb(255 255 255 / 10%);
        }

        @for $i from 1 through 8 {
            &:nth-child(#{$i}) {
                transition-delay: (40ms * $i);
            }
        }

        .animate-in & {
            opacity: 1;
            transform: translate3d(0, 0, 0);
        }
    }

    &__vendor-logo {
        object-fit: contain;
        width: 100%;
        height: auto;
        max-height: 100%;
        max-width: 100%;

        &.logo--dark {
            filter: invert(1);
        }
    }

    &__loader {
        align-self: center;
        margin-top: 40px;
    }
}
</style>
